<template>
  <div>
    <x-toolbar />
    <v-card>
      <v-container>
        <v-form
          ref="form"
          v-model="valid"
          @submit.prevent
        >
          <FormFields
            ref="fields"
            :form="form"
            :render="formRender"
            lang-path="tasks.moveProducts."
          >
            <template #transfer_mode-item="{ item }">
              <TaskCreateStrictModeItem
                :item="item"
                lang-path="tasks.moveProducts.strictHint."
              />
            </template>
          </FormFields>
          <v-flex
            xs12
          >
            <v-divider
              class="my-3"
            />
            <ProductInstancesChips
              :items="currentLocationItems"
              :loading="loadingLocationItems"
              lang-path="stocks.locations.items"
              can-load-locations
              :sub-stock-id="form.subordinate_stock_id"
            />
          </v-flex>
          <v-flex
            xs12
          >
            <TaskChooseItems
              :chosen-items.sync="chosenItems"
              :available-items="currentLocationItems"
              title-path="tasks.moveProducts.items.productsToMove"
              :loading="loading"
              :loading-items="loadingLocationItems"
              items-from-stock-status
              :stock-id="form.stock_id"
              :sub-stock-id="form.subordinate_stock_id"
              :destination-location-id="form.destination_location_id"
              :allow-empty="form.transfer_mode === TaskItemsStrictMode.FREE"
            >
              <template #item="{ item }">
                <v-layout>
                  <v-flex>
                    {{ item.instance | instanceLabel }}
                  </v-flex>
                  <v-flex class="text-right ml-2">
                    {{ item.quantity }}
                  </v-flex>
                </v-layout>
              </template>
            </TaskChooseItems>
          </v-flex>
          <v-flex
            xs12
          >
            <v-divider
              class="my-3"
            />
            <TaskAttachmentUpload
              v-model="validAttachments"
              :files-to-attach="form.attachments"
            />
          </v-flex>
          <v-flex
            xs12
          >
            <v-divider
              class="my-3"
            />
            <v-btn
              :text="!validAll"
              :loading="loading"
              type="submit"
              color="accent"
              @click="submit()"
            >
              <v-icon
                class="mr-2"
              >
                $taskMoveProducts
              </v-icon>
              {{ $t('tasks.moveProducts.create') }}
            </v-btn>
          </v-flex>
        </v-form>
      </v-container>
    </v-card>
    <div class="my-2">
      <StockLocationCreateUpdate
        v-if="showCreateLocation"
        id="locationForm"
        ref="locationForm"
        :is-edit="false"
        is-component
        :parent-stock-id="form.stock_id"
      />
    </div>
  </div>
</template>

<script>
    import FormFields from "@/app/components/form/FormFields.component";
    import {
        MoveProductsCreateForm,
        MoveProductsCreateRender
    } from "@/app/tasks/moveProducts/definitions/moveProductCreate.form";
    import {formMountedProcedure, setFormErrors} from "@/utils/form";
    import {TaskMoveProductsAPI} from "@/api/TaskMoveProductsAPI";
    import {StockAPI} from "@/api/StockAPI";
    import formRules from "@/utils/formRules";
    import {StockStatusAPI} from "@/api/StockStatusAPI";
    import {has} from "@/utils/object";
    import TaskAttachmentUpload from "@/app/tasks/components/TaskAttachmentUpload.component";
    import ProductInstancesChips from "@/app/products/instances/components/ProductInstancesChips.component";
    import {mapReduce} from "@/utils/array";
    import {locationLabel} from "@/utils/string";
    import StockLocationCreateUpdate from "@/app/stocks/locations/StockLocationCreateUpdate.view";
    import {scrollTo} from "@/service/Vuetify";
    import TaskChooseItems from "@/app/tasks/components/TaskChooseItems.component";
    import {taskLinks, taskTypes} from "@/enum/task_type";
    import {getIdFromLocation} from "@/utils/url";
    import TaskCreateStrictModeItem from "@/app/tasks/components/TaskCreateStrictModeItem.component";
    import {TaskItemsStrictMode} from "@/enum/task_items_strict_mode";
    import {EventsListenerMixin} from "@/app/mixins/EventsListenerMixin";

    export default {
        name: "MoveProductsCreate",
        components: {
            TaskCreateStrictModeItem,
            StockLocationCreateUpdate, ProductInstancesChips, TaskAttachmentUpload, FormFields, TaskChooseItems
        },
        mixins: [EventsListenerMixin],
        data: () => ({
            form: new MoveProductsCreateForm,
            formRender: new MoveProductsCreateRender,
            valid: true,
            validAttachments: true,
            loading: false,
            chosenItems: [{product_id: null, product_instance_id: null}],
            formRules: formRules,
            locationItems: {},
            loadingLocationItems: false,
            showCreateLocation: false,
            TaskItemsStrictMode: TaskItemsStrictMode
        }),
        computed: {
            events: function () {
                return {
                    'create-location': this.onCreateLocation,
                    'create-location-created': this.onCreateLocationCreated,
                    'create-location-cancelled': () => this.showCreateLocation = false
                };
            },
            currentLocationItems: function () {
                if (has(this.locationItems, this.form.subordinate_stock_id)
                    && has(this.locationItems[this.form.subordinate_stock_id], this.form.source_location_id)) {
                    if (this.form.source_location_id === -1
                        && this.form.destination_location_id !== undefined
                        && this.form.destination_location_id !== null) {
                        return this.locationItems[this.form.subordinate_stock_id][this.form.source_location_id].map(locItem => {
                            const location = locItem.locations.find(location => location.stock_location.id === this.form.destination_location_id);
                            if (location) {
                                locItem = {...locItem};
                                locItem.quantity = locItem.quantity - location.quantity;
                            }
                            return locItem;
                        }).filter(locItem => locItem.quantity !== 0);
                    }
                    return this.locationItems[this.form.subordinate_stock_id][this.form.source_location_id];
                } else {
                    return [];
                }
            },
            validAll: function () {
                return this.valid && this.validAttachments;
            }
        },
        watch: {
            'form.stock_id': function (newValue) {
                if (newValue !== undefined && newValue !== null) {
                    this.stockIdChanged();
                }
            },
            'form.subordinate_stock_id': function (newValue) {
                if (newValue !== undefined && newValue !== null) {
                    this.subStockIdChanged();
                }
            },
            'form.source_location_id': function (newValue) {
                if (newValue !== undefined && newValue !== null) {
                    this.sourceLocationChanged();
                }
            }
        },
        mounted: function () {
            formMountedProcedure(this);
        },
        methods: {
            onCreateLocation: function () {
                if (this.form.stock_id !== undefined && this.form.stock_id !== null
                    && this.form.subordinate_stock_id !== undefined && this.form.subordinate_stock_id !== null) {
                    this.showCreateLocation = true;
                    if (this.$refs.fields !== undefined) {
                        this.$refs.fields.$refs.destination_location_id[0].blur();
                    }
                    this.$nextTick(() => {
                        scrollTo('locationForm');
                    });
                } else {
                    this.snack('stocks.locations.create.chooseStockAndSubstockFirst');
                }
            },
            onCreateLocationCreated: function (locationId) {
                this.showCreateLocation = false;
                StockAPI.getAllSubstockWhitelistedLocations(this.form.stock_id, this.form.subordinate_stock_id).then(response => {
                    const promises = [];
                    if (response.data.length !== 0) {
                        promises.push(
                            StockAPI.createSubstockWhitelistLocation(this.form.stock_id, this.form.subordinate_stock_id, locationId)
                        );
                    }
                    Promise.all(promises).then(() => {
                        this.loadLocations();
                        this.form.destination_location_id = Number.parseInt(locationId, 10);
                    });
                });
            },
            stockIdChanged: function () {
                this.form.subordinate_stock_id = null;
                this.loadSubStocks();
            },
            subStockIdChanged: function () {
                this.loadLocations();
                if (this.form.source_location_id !== undefined && this.form.source_location_id !== null) {
                    this.loadLocationItems();
                }
            },
            sourceLocationChanged: function () {
                if (this.form.subordinate_stock_id !== undefined && this.form.subordinate_stock_id !== null) {
                    this.loadLocationItems();
                }
            },
            loadSubStocks: function () {
                this.$set(this.formRender.subordinate_stock_id, 'loading', true);
                StockAPI.getAllSubstockPages(this.form.stock_id)
                    .then(response => {
                        this.formRender.subordinate_stock_id.autocomplete.items = response.data.items.map(el => ({
                            text: el.name,
                            value: el.id
                        }));
                    }).finally(() => {
                        this.$set(this.formRender.subordinate_stock_id, 'loading', false);
                    });
            },
            loadLocations: function () {
                this.$set(this.formRender.source_location_id, 'loading', true);
                this.$set(this.formRender.destination_location_id, 'loading', true);
                StockAPI.getAllSubstockAvailableLocationsAllPages(this.form.stock_id, this.form.subordinate_stock_id)
                    .then(response => {
                        const items = [{
                            text: this.$t('tasks.moveProducts.anyLocation'),
                            value: -1
                        }].concat(response.data.items.map(el => ({
                            text: locationLabel(el),
                            value: el.id
                        })));
                        this.formRender.source_location_id.autocomplete.items = items;
                        this.formRender.destination_location_id.autocomplete.items = items;
                    }).finally(() => {
                        this.$set(this.formRender.source_location_id, 'loading', false);
                        this.$set(this.formRender.destination_location_id, 'loading', false);
                    });
            },
            loadLocationItems: function () {
                const [ssId, locId] = [this.form.subordinate_stock_id, this.form.source_location_id];
                if (!has(this.locationItems, ssId)) {
                    this.$set(this.locationItems, ssId, {});
                }
                if (!has(this.locationItems[ssId], locId)) {
                    this.$set(this.locationItems[ssId], locId, []);
                }
                if (this.locationItems[ssId][locId].length === 0) {
                    this.loadingLocationItems = true;
                    const promise = locId === -1 ? StockStatusAPI.getCurrentInStock(ssId) : StockStatusAPI.getCurrentOnLocation(ssId, locId);
                    promise
                        .then(response => {
                            response.data.items = response.data.items.filter(stockStatus => !has(stockStatus.stock_location, 'user'));
                            if (locId === -1) {
                                //locations for bottom sheet, gather them only, when we have requested information for all locations
                                const locations = {};
                                response.data.items.forEach(stockStatus => {
                                    if (has(stockStatus.stock_location, 'user') || stockStatus.quantity === 0) {
                                        return;
                                    }
                                    if (locations[stockStatus.product_instance.id] === undefined) {
                                        locations[stockStatus.product_instance.id] = [{...stockStatus}];
                                    } else {
                                        locations[stockStatus.product_instance.id].push({...stockStatus});
                                    }
                                });
                                response.data.items = mapReduce(response.data.items, ['product_instance', 'id'], 'quantity');
                                response.data.items = response.data.items.map(item => {
                                    item.locations = locations[item.product_instance.id];
                                    return item;
                                });
                            } else {
                                response.data.items = response.data.items.filter(stockStatus => stockStatus.stock_location.id === locId);
                            }
                            this.$set(this.locationItems[ssId], locId, response.data.items
                                .map(el => ({
                                    instance: el.product_instance,
                                    locations: locId === -1 ? el.locations : undefined,
                                    quantity: el.quantity,
                                    value: el.product_instance.id
                                }))
                                .filter(product => product.quantity > 0)
                                .sort((a, b) => 0.5 - (
                                    this.$options.filters.instanceLabel(a.instance) < this.$options.filters.instanceLabel(b.instance)
                                )));
                        }).catch(this.snack)
                        .finally(() => {
                            this.loadingLocationItems = false;
                        });
                }
            },
            submit: function () {
                if (!this.validAll) {
                    this.$refs.form.validate();
                    return;
                }
                if (this.form.source_location_id === this.form.destination_location_id) {
                    if (this.form.source_location_id !== -1) {
                        this.snack('tasks.moveProducts.unableToMoveToSame');
                        return;
                    }
                }
                this.loading = true;
                const data = {...this.form};
                if (data.source_location_id === -1) {
                    data.source_location_id = null;
                }
                if (data.destination_location_id === -1) {
                    data.destination_location_id = null;
                }
                data.items = this.chosenItems
                    .filter(item => {
                        delete item.errors;
                        delete item.product_id;
                        return item.product_instance_id !== null;
                    });
                TaskMoveProductsAPI.create(data)
                    .then(response => {
                        this.snack('tasks.moveProducts.createDone');
                        this.$route.meta.uuid = this.$route.meta.uuid + 1 || 1;
                        this.$router.push(taskLinks[taskTypes.MOVE_PRODUCTS] + '/' + getIdFromLocation(response));
                    }).catch(err => {
                        // error is not handled, need to get errors manually
                        if (err.response.status === 409) {
                            const problems = err.response.data.blocking_items || [];
                            this.chosenItems.filter(item => problems.find(problem => problem.instance_id === item.product_instance_id)).forEach(item => {
                                this.$set(item, 'errors', [this.$t('tasks.moveProducts.notEnoughItems')]);
                            });
                        } else {
                            setFormErrors.call(this, err.response.data.errors);
                        }
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            }
        }
    };
</script>

<style scoped>

</style>
